import { useState } from "react";
import { toast } from "sonner";
import {
  Bell,
  CheckCircle2,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  Palette,
  Phone,
  ShieldCheck,
  UserRound,
  XCircle,
} from "lucide-react";

import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

import { ROLE_LABEL } from "@/lib/constants";


function PasswordInput({
  id,
  value,
  onChange,
  show,
  onToggle,
  placeholder,
  minLength,
  maxLength,
}) {
  return (
    <div className="relative">
      <Input
        id={id}
        data-testid={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        minLength={minLength}
        maxLength={maxLength}
        autoComplete="new-password"
        className="pr-10"
      />

      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
        aria-label={show ? "Hide password" : "Show password"}
        title={show ? "Hide Password" : "Show Password"}
      >
        {show ? (
          <EyeOff className="h-4 w-4" />
        ) : (
          <Eye className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}


export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const { theme, setTheme } = useTheme();

  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    employee_code: user?.employee_code || "",
  });

  const [prefs, setPrefs] = useState(
    user?.notification_prefs || {
      email: true,
      whatsapp: true,
      in_app: true,
    }
  );

  const [pw, setPw] = useState({
    current: "",
    next: "",
    confirm: "",
  });

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const saveProfile = async () => {
    try {
      await api.patch("/profile", {
        ...form,
        notification_prefs: prefs,
        theme,
      });

      toast.success("Profile updated");
      refresh();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };


  const changePassword = async () => {
    if (!pw.current || !pw.next || !pw.confirm) {
      return toast.error(
        "Fill in current, new and confirm password"
      );
    }

    if (pw.next.length < 8) {
      return toast.error(
        "New password must be at least 8 characters"
      );
    }

    if (pw.next.length > 16) {
      return toast.error(
        "New password must not exceed 16 characters"
      );
    }

    if (pw.current === pw.next) {
      return toast.error(
        "New password must be different from the current password"
      );
    }

    if (pw.next !== pw.confirm) {
      return toast.error("Passwords do not match");
    }

    try {
      await api.post(
        "/profile/change-password",
        {
          current_password: pw.current,
          new_password: pw.next,
        }
      );

      toast.success("Password changed");

      setPw({
        current: "",
        next: "",
        confirm: "",
      });

      setShowCurrent(false);
      setShowNext(false);
      setShowConfirm(false);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };


  if (!user) return null;


  const nextLengthValid =
    pw.next.length >= 8 &&
    pw.next.length <= 16;

  const passwordDifferent =
    Boolean(pw.next) &&
    Boolean(pw.current) &&
    pw.current !== pw.next;

  const passwordMatches =
    Boolean(pw.confirm) &&
    pw.next === pw.confirm;


  const initials = String(
    user.name || user.email || "U"
  )
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");


  return (
    <div
      className="space-y-6"
      data-testid="profile-page"
    >

      {/* Page header */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Account
        </div>

        <h1 className="font-display text-3xl font-extrabold tracking-tight">
          Profile &amp; Preferences
        </h1>

        <p className="mt-1 text-sm text-muted-foreground">
          Manage your profile, notifications, appearance and password.
        </p>
      </div>


      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.45fr)_minmax(340px,0.8fr)]">

        {/* ===================================================
            LEFT COLUMN
            =================================================== */}
        <div className="space-y-6">

          {/* Personal Details */}
          <section className="rounded-xl border border-border bg-card/70 p-5">

            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary">
                <UserRound className="h-4 w-4" />
              </div>

              <div>
                <h2 className="text-sm font-semibold">
                  Personal Details
                </h2>

                <p className="mt-0.5 text-xs text-muted-foreground">
                  Keep your profile and contact details up to date.
                </p>
              </div>
            </div>


            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

              <div className="space-y-1.5">
                <Label>Full Name</Label>

                <Input
                  data-testid="profile-name"
                  value={form.name}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      name: event.target.value,
                    })
                  }
                />
              </div>


              <div className="space-y-1.5">
                <Label>Phone</Label>

                <Input
                  data-testid="profile-phone"
                  value={form.phone}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      phone: event.target.value,
                    })
                  }
                  placeholder="+91..."
                />
              </div>


              <div className="space-y-1.5">
                <Label>Employee Code</Label>

                <Input
                  data-testid="profile-employee-code"
                  value={form.employee_code}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      employee_code: event.target.value,
                    })
                  }
                />
              </div>


              <div className="space-y-1.5">
                <Label>Email</Label>

                <Input
                  value={user.email}
                  disabled
                />

                <div className="text-[11px] text-muted-foreground">
                  Email cannot be changed from this screen.
                </div>
              </div>
            </div>


            <div className="mt-5 flex justify-end border-t border-border pt-4">
              <Button
                data-testid="profile-save"
                onClick={saveProfile}
              >
                Save Profile
              </Button>
            </div>
          </section>


          {/* Notification Preferences */}
          <section className="rounded-xl border border-border bg-card/70 p-5">

            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary">
                <Bell className="h-4 w-4" />
              </div>

              <div>
                <h2 className="text-sm font-semibold">
                  Notification Preferences
                </h2>

                <p className="mt-0.5 text-xs text-muted-foreground">
                  Choose how you want Miledeep Help Desk to notify you.
                </p>
              </div>
            </div>


            <div className="divide-y divide-border rounded-lg border border-border">

              {[
                {
                  key: "in_app",
                  label: "In-App Notifications",
                  description:
                    "Receive notifications inside the Help Desk application.",
                  icon: Bell,
                },
                {
                  key: "email",
                  label: "Email Notifications",
                  description:
                    "Receive ticket and activity updates by email.",
                  icon: Mail,
                },
                {
                  key: "whatsapp",
                  label: "WhatsApp Notifications",
                  description:
                    "Receive supported notifications through WhatsApp.",
                  icon: Phone,
                },
              ].map((preference) => {
                const Icon = preference.icon;

                return (
                  <div
                    key={preference.key}
                    className="flex items-center justify-between gap-4 px-4 py-3"
                  >
                    <div className="flex min-w-0 items-start gap-3">

                      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary/60 text-muted-foreground">
                        <Icon className="h-3.5 w-3.5" />
                      </div>

                      <div>
                        <Label>
                          {preference.label}
                        </Label>

                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {preference.description}
                        </p>
                      </div>
                    </div>

                    <Switch
                      data-testid={`profile-pref-${preference.key}`}
                      checked={Boolean(
                        prefs[preference.key]
                      )}
                      onCheckedChange={(value) =>
                        setPrefs({
                          ...prefs,
                          [preference.key]: value,
                        })
                      }
                    />
                  </div>
                );
              })}
            </div>
          </section>


          {/* Appearance */}
          <section className="rounded-xl border border-border bg-card/70 p-5">

            <div className="mb-4 flex items-center gap-3">

              <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary">
                <Palette className="h-4 w-4" />
              </div>

              <div>
                <h2 className="text-sm font-semibold">
                  Appearance
                </h2>

                <p className="mt-0.5 text-xs text-muted-foreground">
                  Choose the theme applied across every screen.
                </p>
              </div>
            </div>


            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">

              {[
                {
                  key: "midnight",
                  label: "Midnight Navy",
                  description: "Deep navy workspace",
                },
                {
                  key: "light",
                  label: "Light",
                  description: "Bright workspace",
                },
                {
                  key: "classic",
                  label: "Classic Dark",
                  description: "Neutral dark workspace",
                },
              ].map((option) => (
                <button
                  type="button"
                  key={option.key}
                  data-testid={`profile-theme-${option.key}`}
                  onClick={() =>
                    setTheme(option.key)
                  }
                  className={`rounded-xl border p-4 text-left transition ${
                    theme === option.key
                      ? "border-primary bg-primary/10 shadow-sm"
                      : "border-border bg-background/20 hover:border-primary/40 hover:bg-secondary/30"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">

                    <div className="text-sm font-semibold">
                      {option.label}
                    </div>

                    {theme === option.key && (
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                    )}
                  </div>

                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {option.description}
                  </div>
                </button>
              ))}
            </div>
          </section>
        </div>


        {/* ===================================================
            RIGHT COLUMN
            =================================================== */}
        <aside className="space-y-6">

          {/* Account summary */}
          <section className="rounded-xl border border-border bg-card/70 p-5">

            <div className="flex flex-col items-center text-center">

              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-lg font-bold text-primary">
                {initials}
              </div>

              <h2 className="mt-3 font-display text-lg font-bold">
                {user.name || "User"}
              </h2>

              <div className="mt-1 text-xs text-muted-foreground">
                {user.email}
              </div>

              <span className="mt-3 inline-flex rounded-full border border-border bg-secondary/40 px-3 py-1 text-xs font-medium">
                {ROLE_LABEL[user.role]}
              </span>
            </div>


            <div className="mt-5 space-y-3 border-t border-border pt-4 text-xs">

              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">
                  Account
                </span>

                <span className="inline-flex items-center gap-1.5 font-medium text-emerald-500">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Active
                </span>
              </div>


              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">
                  Employee Code
                </span>

                <span className="font-medium">
                  {form.employee_code || "—"}
                </span>
              </div>
            </div>
          </section>


          {/* Password */}
          <section className="rounded-xl border border-border bg-card/70 p-5">

            <div className="mb-5 flex items-center gap-3">

              <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary">
                <LockKeyhole className="h-4 w-4" />
              </div>

              <div>
                <h2 className="text-sm font-semibold">
                  Change Password
                </h2>

                <p className="mt-0.5 text-xs text-muted-foreground">
                  Update the password used to sign in.
                </p>
              </div>
            </div>


            <div className="space-y-4">

              <div className="space-y-1.5">

                <Label htmlFor="password-current">
                  Current Password
                </Label>

                <PasswordInput
                  id="password-current"
                  value={pw.current}
                  onChange={(event) =>
                    setPw({
                      ...pw,
                      current: event.target.value,
                    })
                  }
                  show={showCurrent}
                  onToggle={() =>
                    setShowCurrent(
                      (current) => !current
                    )
                  }
                  placeholder="Enter current password"
                />
              </div>


              <div className="space-y-1.5">

                <Label htmlFor="password-new">
                  New Password
                </Label>

                <PasswordInput
                  id="password-new"
                  value={pw.next}
                  onChange={(event) =>
                    setPw({
                      ...pw,
                      next: event.target.value,
                    })
                  }
                  show={showNext}
                  onToggle={() =>
                    setShowNext(
                      (current) => !current
                    )
                  }
                  placeholder="Enter 8–16 characters"
                  minLength={8}
                  maxLength={16}
                />
              </div>


              <div className="space-y-1.5">

                <Label htmlFor="password-confirm">
                  Confirm New Password
                </Label>

                <PasswordInput
                  id="password-confirm"
                  value={pw.confirm}
                  onChange={(event) =>
                    setPw({
                      ...pw,
                      confirm: event.target.value,
                    })
                  }
                  show={showConfirm}
                  onToggle={() =>
                    setShowConfirm(
                      (current) => !current
                    )
                  }
                  placeholder="Re-enter new password"
                  minLength={8}
                  maxLength={16}
                />
              </div>


              {/* Password requirements */}
              <div className="rounded-lg border border-border bg-secondary/20 p-3">

                <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Password Requirements
                </div>

                <div className="space-y-2 text-xs">

                  <Requirement
                    passed={nextLengthValid}
                    label="8–16 characters"
                  />

                  <Requirement
                    passed={passwordDifferent}
                    label="Different from current password"
                    neutral={!pw.current || !pw.next}
                  />

                  <Requirement
                    passed={passwordMatches}
                    label="New passwords match"
                    neutral={!pw.confirm}
                  />

                </div>
              </div>


              <Button
                data-testid="password-save"
                onClick={changePassword}
                className="w-full"
                disabled={
                  !pw.current ||
                  !pw.next ||
                  !pw.confirm ||
                  !nextLengthValid ||
                  !passwordDifferent ||
                  !passwordMatches
                }
              >
                <ShieldCheck className="mr-2 h-4 w-4" />
                Update Password
              </Button>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}


function Requirement({
  passed,
  label,
  neutral = false,
}) {
  return (
    <div className="flex items-center gap-2">

      {neutral ? (
        <span className="flex h-4 w-4 items-center justify-center rounded-full border border-border" />
      ) : passed ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
      ) : (
        <XCircle className="h-4 w-4 text-rose-500" />
      )}

      <span
        className={
          neutral
            ? "text-muted-foreground"
            : passed
              ? "text-emerald-600"
              : "text-rose-500"
        }
      >
        {label}
      </span>
    </div>
  );
}
